/**
 * GxProgressSteps: one component for every "where am I in these steps" strip. Each step is done, current or open, and jumps to its step.
 * `bar` (default): one thin segment per step, for many steps (the check-in's situations). `labelled`: a pill of numbered, named steps with an optional note
 * (the Decide round: Your forecast · Decide · Review & sign). Style lives in theme.css.
 */
import { Icon } from "./icons";

export interface ProgressStep {
  readonly label: string;
  /** Finished. The current step is shown as current whatever this says. */
  readonly done: boolean;
  /** Labelled only: small text after the label, with its own separator ("· 3 to go"). */
  readonly note?: string | undefined;
  /** Labelled only: what the circle shows while the step is not done; defaults to its place in the list. */
  readonly mark?: string | undefined;
}

export function GxProgressSteps({ steps, current, onSelect, variant = "bar", label = "Progress", testId }: {
  readonly steps: readonly ProgressStep[]; readonly current: number;
  /** Omit it and the steps are plain, not links. */
  readonly onSelect?: ((index: number) => void) | undefined;
  readonly variant?: "bar" | "labelled"; readonly label?: string; readonly testId?: string;
}) {
  if (variant === "labelled") {
    return (
      <ol className="gx-steps gx-steps--labelled" aria-label={label} data-testid={testId}>
        {steps.map((s, i) => {
          const state = i === current ? "now" : s.done ? "done" : "todo";
          const inner = <><span className="gx-step-n">{state === "done" ? <Icon name="check" size={12} /> : s.mark ?? i + 1}</span>{s.label}{s.note === undefined ? null : <small>{s.note}</small>}</>;
          return (
            <li key={s.label}>
              {i === current || onSelect === undefined
                ? <div className="gx-step-pill" data-state={state} aria-current={i === current ? "step" : undefined}>{inner}</div>
                : <button type="button" className="gx-step-pill gx-step-pill--btn" data-state={state} onClick={() => onSelect(i)}>{inner}</button>}
            </li>
          );
        })}
      </ol>
    );
  }
  return (
    <ol className="gx-steps" aria-label={label} data-testid={testId}>
      {steps.map((s, i) => (
        <li key={s.label}>
          <button type="button" className="gx-step" data-done={s.done} aria-current={i === current ? "step" : undefined} aria-label={`${s.label}${s.done ? ", answered" : ""}`} onClick={() => onSelect?.(i)} />
        </li>
      ))}
    </ol>
  );
}
